// src/components/WZStatisticsExtended.js
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { SelectButton } from "primereact/selectbutton";
import { ToggleButton } from "primereact/togglebutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Cell,
} from "recharts";
import * as XLSX from "xlsx";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { saveAs } from "file-saver";
import { baseURL } from "../utils/api";
import { useMediaQuery } from "react-responsive";
import { MultiSelect } from "primereact/multiselect";
import "../App.css";
import "../css/WZStatistics.css";
import {
  locale,
  addLocale,
  updateLocaleOption,
  updateLocaleOptions,
  localeOption,
  localeOptions,
} from "primereact/api";

// Dodanie lokalizacji dla kalendarza
addLocale("pl", {
  firstDayOfWeek: 1,
  dayNames: [
    "Niedziela",
    "Poniedziałek",
    "Wtorek",
    "Środa",
    "Czwartek",
    "Piątek",
    "Sobota",
  ],
  dayNamesShort: ["Niedz.", "Pon.", "Wto.", "Śro.", "Czw.", "Pią.", "Sob."],
  dayNamesMin: ["Nd", "Pn", "Wt", "Śr", "Cz", "Pt", "So"],
  monthNames: [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień",
  ],
  monthNamesShort: [
    "Sty",
    "Lut",
    "Mar",
    "Kwi",
    "Maj",
    "Cze",
    "Lip",
    "Sie",
    "Wrz",
    "Paź",
    "Lis",
    "Gru",
  ],
  chooseYear: "Wybierz rok",
  chooseMonth: "Wybierz miesiąc",
  chooseDate: "Wybierz datę",
  chooseTime: "Wybierz czas",
  today: "Dziś",
  clear: "Wyczyść",
  weekHeader: "Tydz",
  dateFormat: "dd.mm.yy",
  weekFormat: "W",
  dateIsEmpty: "Data jest pusta",
  dateIsInvalid: "Data jest nieprawidłowa",
  dateIsOutOfRange: "Data jest poza zakresem",
});

const WZStatisticsExtended = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { token } = useContext(AuthContext);
  const options = ["WZ", "RW"];
  const [docType, setDocType] = useState(options[0]);

  // Główne stany
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Opcje dropdown
  const [firmyOptions, setFirmyOptions] = useState([]);
  const [oddzialOptions, setOddzialOptions] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [przewoznicyOptions, setPrzewoznicyOptions] = useState([]);
  const [kontrahenciOptions, setKontrahenciOptions] = useState([]);

  // Wybrane filtry
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [selectedOddzial, setSelectedOddzial] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedKontrahent, setSelectedKontrahent] = useState(null);
  const [selectedPrzewoznik, setSelectedPrzewoznik] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isPrzewoznik, setIsPrzewoznik] = useState(false);
  const [statsByOddzialMaterial, setStatsByOddzialMaterial] = useState([]);
  const [statsByOddzialTotal, setStatsByOddzialTotal] = useState([]);

  // Agregacje danych
  const [clientDocRecords, setClientDocRecords] = useState([]);
  const [detailedStats, setDetailedStats] = useState([]);
  const [extendedStats, setExtendedStats] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [printMode, setPrintMode] = useState(null); // null | "single" | "all"
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printClients, setPrintClients] = useState([]);
  const [printSummaryOnly, setPrintSummaryOnly] = useState(true);

  // Pobieranie cennika
  useEffect(() => {
    axios
      .get(`${baseURL}api/cennik`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setPriceList(response.data))
      .catch((error) => console.error("Błąd pobierania cennika:", error));
  }, [token]);

  const enrichedClientDocRecords = clientDocRecords.map((rec) => {
    // Cena ogólna kontrahenta (przeznaczenie = NULL)
    const cena = priceList.find(
      (p) =>
        p.kontrahentID === selectedKontrahent?.id &&
        p.material === rec["Materiał"] &&
        !p.przeznaczenieID
    )?.cena;

    // Cena dedykowana przeznaczeniu dokumentu (bez fallbacku do ceny ogólnej)
    const cenaPrzezn = priceList.find(
      (p) =>
        p.kontrahentID === selectedKontrahent?.id &&
        p.material === rec["Materiał"] &&
        p.przeznaczenie &&
        p.przeznaczenie.trim() === (rec["Przeznaczenie"] || "").trim()
    )?.cena;

    return {
      ...rec,
      "Cena z cennika": cena != null ? Number(cena) : null,
      "Wartość netto (cennikowa)":
        cena != null ? Number(cena) * rec["Ilość"] : null,
      "Cena wg przeznaczenia": cenaPrzezn != null ? Number(cenaPrzezn) : null,
      "Wartość wg przeznaczenia":
        cenaPrzezn != null ? Number(cenaPrzezn) * rec["Ilość"] : null,
    };
  });

  // Funkcja agregująca dokumenty dla materiału
  const aggregateMaterialDocuments = (docs, materialId) => {
    const records = [];
    docs.forEach((doc) => {
      if (Array.isArray(doc.products) && doc.odbiorca && doc.odbiorca.nazwa) {
        doc.products.forEach((product) => {
          if (
            product.nazwaMaterialu &&
            product.nazwaMaterialu.id === materialId
          ) {
            records.push({
              Materiał: product.nazwaMaterialu.nazwa,
              Klient: doc.odbiorca.nazwa,
              "Data wystawienia": new Date(
                doc.dataWystawienia
              ).toLocaleDateString("pl-PL"),
              "Numer WZ": doc.numerWZ,
              Opłacono: doc.oplacono,
              "Kwota brutto": doc.kwotaBrutto || 0,
              "Kwota netto": doc.kwotaNetto || 0,
              Uwagi: doc.uwagi || "",
              Przewoźnik: doc.przewoznik
                ? JSON.parse(doc.przewoznik).nazwa
                : null,
            });
          }
        });
      }
    });
    return records;
  };

  // Funkcja agregująca dokumenty dla klienta
  const aggregateClientDocuments = (docs, clientValue) => {
    const records = [];
    docs.forEach((doc) => {
      if (
        doc.odbiorca &&
        doc.odbiorca.nazwa === clientValue &&
        Array.isArray(doc.products)
      ) {
        doc.products.forEach((product) => {
          if (product.nazwaMaterialu && product.nazwaMaterialu.nazwa) {
            records.push({
              Materiał: product.nazwaMaterialu.nazwa,
              Ilość: Number(product.ilosc) || 0,
              "Data wystawienia": new Date(
                doc.dataWystawienia
              ).toLocaleDateString("pl-PL"),
              "Numer WZ": doc.numerWZ,
              Firma: doc.firma?.nazwa || "",
              Przeznaczenie: doc.przeznaczenie || "",
              Opłacono: doc.oplacono,
              "Kwota brutto": doc.kwotaBrutto || 0,
              "Kwota netto": doc.kwotaNetto || 0,
              Uwagi: doc.uwagi || "",
            });
          }
        });
      }
    });
    return records;
  };

  // Pobieranie dokumentów WZ z backendu i parsowanie zagnieżdżonych pól JSON
  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    setSelectedFirma(null);
    setSelectedOddzial(null);
    setSelectedClient(null);
    setSelectedMaterial(null);
    setSelectedPrzewoznik(null);
    setSelectedKontrahent(null);

    setFilteredDocs([]);
    setClientDocRecords([]);
    setDetailedStats([]);
    setExtendedStats([]);
    setDocuments([]);
    setPrzewoznicyOptions([]);

    axios
      .get(`${baseURL}api/wz?typ=${docType}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        // Parsujemy zagnieżdżone pola, jeżeli są zwracane jako string JSON
        const docs = response.data.map((doc) => {
          const parsedProducts =
            typeof doc.products === "string"
              ? JSON.parse(doc.products)
              : doc.products;
          parsedProducts.map((prod) => {
            if (prod && typeof prod.nazwaMaterialu === "string") {
              try {
                prod.nazwaMaterialu = JSON.parse(prod.nazwaMaterialu);
              } catch (error) {
                console.error("Błąd parsowania nazwaMaterialu:", error);
              }
            }
            return prod;
          });
          return {
            ...doc,
            odbiorca:
              typeof doc.odbiorca === "string"
                ? JSON.parse(doc.odbiorca)
                : doc.odbiorca,
            firma:
              typeof doc.firma === "string" ? JSON.parse(doc.firma) : doc.firma,
            egz: typeof doc.egz === "string" ? JSON.parse(doc.egz) : doc.egz,
            transport:
              typeof doc.transport === "string"
                ? JSON.parse(doc.transport)
                : doc.transport,
            products: parsedProducts,
          };
        });
        setDocuments(docs);
        const przewoznicyOptionsTemp = docs.map((doc) => {
          if (doc.isPrzewoznik) {
            const przewoznik = JSON.parse(doc.przewoznik);
            return {
              label: przewoznik?.nazwa,
              value: przewoznik?.nazwa,
            };
          }
        });

        const uniquePrzewoznicy = new Set(
          przewoznicyOptionsTemp.map((item) => item?.value)
        );
        const uniquePrzewoznicyOptions = [...uniquePrzewoznicy].map((value) => {
          return przewoznicyOptionsTemp.find((item) => item?.value === value);
        });
        setPrzewoznicyOptions(uniquePrzewoznicyOptions.filter(Boolean));
      })

      .catch((error) => console.error("Błąd pobierania dokumentów:", error));
  }, [token, docType]);

  // Pobieranie listy firm
  useEffect(() => {
    axios
      .get(`${baseURL}api/firmy`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setFirmyOptions(
          response.data.map((firma) => ({
            label: firma.nazwa,
            value: firma.id,
          }))
        );
      })
      .catch((error) => console.error("Błąd pobierania firm:", error));
  }, [token]);

  // Pobieranie listy oddziałów (egz)
  useEffect(() => {
    axios
      .get(`${baseURL}api/egz`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setOddzialOptions(
          response.data.map((oddzial) => ({
            label: oddzial.adres,
            value: oddzial.id,
          }))
        );
      })
      .catch((error) => console.error("Błąd pobierania oddziałów:", error));
  }, [token]);

  // Pobieranie listy kontrahentów
  useEffect(() => {
    axios
      .get(`${baseURL}api/kontrahenci`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) =>
        setKontrahenciOptions(response.data.filter((k) => k.dostepny))
      )
      .catch((error) => console.error("Błąd pobierania kontrahentów:", error));
  }, [token]);

  // Pobieranie listy materiałów
  useEffect(() => {
    axios
      .get(`${baseURL}api/materialy`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setMaterialOptions(
          response.data.map((material) => ({
            label: material.nazwa,
            value: material.id,
          }))
        );
      })
      .catch((error) => console.error("Błąd pobierania materiałów:", error));
  }, [token]);

  // Filtrowanie dokumentów wg dat, firmy, oddziału i materiału
  useEffect(() => {
    let docs = [...documents];
    if (startDate || endDate) {
      docs = docs.filter((doc) => {
        //ustawienie godziny na 00:00:00 dla daty początkowej i na 23:59:59 dla końcowej;
        const docDate = new Date(doc.dataWystawienia);
        if (startDate && endDate) {
          return (
            docDate >= new Date(startDate) &&
            docDate <= new Date(endDate).setHours(23, 59, 59, 999)
          );
        } else if (startDate) {
          return docDate >= new Date(startDate);
        } else if (endDate) {
          return docDate <= new Date(endDate).setHours(23, 59, 59, 999);
        }
        return true;
      });
    }
    if (selectedFirma) {
      docs = docs.filter(
        (doc) => doc.firma && doc.firma.id === selectedFirma.value
      );
    }
    if (selectedOddzial) {
      docs = docs.filter(
        (doc) => doc.egz && doc.egz.id === selectedOddzial.value
      );
    }
    if (selectedMaterial) {
      docs = docs.filter((doc) =>
        doc.products.some(
          (product) =>
            product.nazwaMaterialu &&
            product.nazwaMaterialu.id === selectedMaterial.value
        )
      );
    }
    if (selectedPrzewoznik?.value.length > 0) {
      docs = docs.filter((doc) => {
        if (!doc.isPrzewoznik) return false;
        if (!doc.przewoznik) return false;
        return selectedPrzewoznik.value.some(
          (selected) => selected === JSON.parse(doc.przewoznik)?.nazwa
        );
      });
    }
    setFilteredDocs(docs);
  }, [
    startDate,
    endDate,
    selectedFirma,
    selectedOddzial,
    documents,
    selectedMaterial,
    selectedPrzewoznik,
  ]);

  const aggregateByCustomerAndMaterial = (docs) => {
    const result = {};
    docs.forEach((doc) => {
      if (doc.odbiorca && doc.odbiorca.nazwa && Array.isArray(doc.products)) {
        const client = doc.odbiorca.nazwa;
        if (!result[client]) {
          result[client] = {};
        }
        doc.products.forEach((product) => {
          if (product.nazwaMaterialu && product.nazwaMaterialu.nazwa) {
            const material = product.nazwaMaterialu.nazwa;
            const qty = Number(product.ilosc) || 0;
            if (!result[client][material]) {
              result[client][material] = 0;
            }
            result[client][material] += qty;
          }
        });
      }
    });
    return Object.entries(result).map(([client, materials]) => ({
      client,
      materials: Object.entries(materials).map(([material, quantity]) => ({
        material,
        quantity,
      })),
    }));
  };

  const aggregateByClientOddzialAndMaterial = (docs) => {
    const result = [];
    docs.forEach((doc) => {
      if (
        doc.firma &&
        doc.odbiorca &&
        doc.odbiorca.nazwa &&
        doc.egz &&
        doc.egz.skrot &&
        Array.isArray(doc.products)
      ) {
        const firmaName = doc.firma.nazwa;
        const client = doc.odbiorca.nazwa;
        const oddzial = doc.egz.skrot;
        doc.products.forEach((product) => {
          if (product.nazwaMaterialu && product.nazwaMaterialu.nazwa) {
            const material = product.nazwaMaterialu.nazwa;
            const qty = Number(product.ilosc) || 0;
            let entry = result.find(
              (item) =>
                item.firma === firmaName &&
                item.client === client &&
                item.oddzial === oddzial &&
                item.material === material
            );
            if (!entry) {
              entry = {
                firma: firmaName,
                client,
                oddzial,
                material,
                quantity: 0,
              };
              result.push(entry);
            }
            entry.quantity += qty;
          }
        });
      }
    });
    return result;
  };

  const aggregateByClientMaterialKopalniaFirm = (docs) => {
    const result = {};
    docs.forEach((doc) => {
      if (
        doc.odbiorca &&
        doc.odbiorca.nazwa &&
        doc.firma &&
        doc.firma.nazwa &&
        doc.kopalnia &&
        doc.kopalnia.nazwa &&
        Array.isArray(doc.products)
      ) {
        const client = doc.odbiorca.nazwa;
        const firmaName = doc.firma.nazwa;
        const kopalniaName = doc.kopalnia.nazwa;
        doc.products.forEach((product) => {
          if (product.nazwaMaterialu && product.nazwaMaterialu.nazwa) {
            const material = product.nazwaMaterialu.nazwa;
            const qty = Number(product.ilosc) || 0;
            const key = `${client}||${material}||${kopalniaName}||${firmaName}`;
            if (!result[key]) {
              result[key] = {
                client,
                material,
                kopalnia: kopalniaName,
                firma: firmaName,
                quantity: 0,
              };
            }
            result[key].quantity += qty;
          }
        });
      }
    });
    return Object.values(result);
  };

  const aggregateByOddzialAndMaterial = (docs, selectedFirma) => {
    const result = [];
    const filteredDocs = selectedFirma
      ? docs.filter((doc) => doc.firma?.id === selectedFirma.value)
      : docs;

    filteredDocs.forEach((doc) => {
      const oddzial = doc.egz?.skrot || "Nieznany";
      doc.products?.forEach((product) => {
        const material = product.nazwaMaterialu?.nazwa || "Nieznany";
        const qty = Number(product.ilosc) || 0;

        let entry = result.find(
          (item) => item.oddzial === oddzial && item.material === material
        );
        if (!entry) {
          entry = { oddzial, material, quantity: 0 };
          result.push(entry);
        }
        entry.quantity += qty;
      });
    });

    return result;
  };

  const aggregateByOddzialTotalMaterial = (docs, selectedFirma) => {
    const result = {};
    const filteredDocs = selectedFirma
      ? docs.filter((doc) => doc.firma?.id === selectedFirma.value)
      : docs;

    filteredDocs.forEach((doc) => {
      const oddzial = doc.egz?.skrot || "Nieznany";
      if (!result[oddzial]) result[oddzial] = 0;
      doc.products?.forEach((product) => {
        result[oddzial] += Number(product.ilosc) || 0;
      });
    });

    return Object.entries(result).map(([oddzial, quantity]) => ({
      oddzial,
      quantity,
    }));
  };

  const getMonthlyExtractionForecast = (docs, selectedFirma) => {
    const monthlyData = {};

    // FILTRUJEMY po firmie, jeśli wybrana
    const filteredDocs = selectedFirma
      ? docs.filter((doc) => doc.firma?.id === selectedFirma.value)
      : docs;

    filteredDocs.forEach((doc) => {
      if (!doc.dataWystawienia || !doc.products) return;
      const date = new Date(doc.dataWystawienia);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;

      if (!monthlyData[key]) monthlyData[key] = 0;

      doc.products.forEach((product) => {
        monthlyData[key] += Number(product.ilosc) || 0;
      });
    });

    const sortedEntries = Object.entries(monthlyData).sort(
      ([a], [b]) => new Date(a) - new Date(b)
    );

    if (sortedEntries.length < 2) return [];

    const n = sortedEntries.length;
    const x = Array.from({ length: n }, (_, i) => i);
    const y = sortedEntries.map(([_, value]) => value);

    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);

    const a = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const b = (sumY - a * sumX) / n;

    const xNext = n;
    const yNext = a * xNext + b;

    const result = sortedEntries.map(([month, quantity], i) => ({
      month,
      quantity,
      forecast: false,
    }));

    const lastMonth = new Date(sortedEntries[n - 1][0] + "-01");
    lastMonth.setMonth(lastMonth.getMonth() + 1);
    const nextMonthKey = `${lastMonth.getFullYear()}-${String(
      lastMonth.getMonth() + 1
    ).padStart(2, "0")}`;

    result.push({
      month: nextMonthKey,
      quantity: yNext,
      forecast: true,
    });

    return result;
  };

  // Agregacja dokumentów w zależności od wybranych filtrów
  useEffect(() => {
    if (filteredDocs.length > 0) {
      if (selectedClient) {
        // Agregacja dla wybranego klienta
        const records = aggregateClientDocuments(
          filteredDocs,
          selectedClient.value
        );
        setClientDocRecords(records);
        setDetailedStats([]);
        setExtendedStats([]);
      } else if (
        selectedFirma ||
        selectedOddzial ||
        selectedMaterial ||
        selectedPrzewoznik
      ) {
        const detailed = aggregateByClientOddzialAndMaterial(filteredDocs);
        setDetailedStats(detailed);
        const extended = aggregateByClientMaterialKopalniaFirm(filteredDocs);
        setExtendedStats(extended);
        setStatsByOddzialMaterial(
          aggregateByOddzialAndMaterial(filteredDocs, selectedFirma)
        );
        setStatsByOddzialTotal(
          aggregateByOddzialTotalMaterial(filteredDocs, selectedFirma)
        );
        setClientDocRecords([]);
      } else {
        setClientDocRecords([]);
        setDetailedStats([]);
        setExtendedStats([]);
      }
    } else {
      setClientDocRecords([]);
      setDetailedStats([]);
      setExtendedStats([]);
    }
  }, [
    filteredDocs,
    selectedClient,
    selectedFirma,
    selectedOddzial,
    selectedMaterial,
    selectedPrzewoznik,
  ]);

  useEffect(() => {
    if (documents.length > 0) {
      const forecast = getMonthlyExtractionForecast(documents, selectedFirma);
      setForecastData(forecast);
    } else {
      setForecastData([]);
    }
  }, [documents, selectedFirma]);

  useEffect(() => {
    if (selectedClient) {
      // Ustawienie kontrahenta na podstawie wybranego klienta
      const kontrahent = kontrahenciOptions.find(
        (k) => k.nazwa === selectedClient.value
      );
      setSelectedKontrahent(kontrahent || null);
    }
  }, [selectedClient, kontrahenciOptions]);

  // Opcje dla dropdowna klienta – generowane na podstawie agregacji
  const clientOptions = aggregateByCustomerAndMaterial(filteredDocs).map(
    (item) => ({
      label: item.client,
      value: item.client,
      item: item,
    })
  );

  // Funkcje drukowania
  const handlePrint = (mode) => {
    setPrintMode(mode);
    setTimeout(() => {
      window.print();
      setPrintMode(null);
    }, 300);
  };

  const openPrintModal = () => {
    const summary = getAllClientsSummary();
    setPrintClients(
      summary.map((c) => ({
        client: c.client,
        selected: true,
        showOplacono: false,
      }))
    );
    setPrintSummaryOnly(true);
    setShowPrintModal(true);
  };

  const handlePrintFromModal = () => {
    setShowPrintModal(false);
    handlePrint("all");
  };

  const getAllClientsSummary = () => {
    const aggregated = aggregateByCustomerAndMaterial(filteredDocs);
    return aggregated.map((clientData) => {
      const kontrahent = kontrahenciOptions.find(
        (k) => k.nazwa === clientData.client
      );
      const materialsWithPrices = clientData.materials.map((mat) => {
        const cena = priceList.find(
          (p) =>
            p.kontrahentID === kontrahent?.id &&
            p.material === mat.material &&
            !p.przeznaczenieID
        )?.cena;
        // Znajdź firmę z dokumentów dla tego materiału i klienta
        const docWithMat = filteredDocs.find(
          (doc) =>
            doc.odbiorca?.nazwa === clientData.client &&
            doc.products?.some(
              (p) => p.nazwaMaterialu?.nazwa === mat.material
            )
        );
        const cenaPrzezn = priceList.find(
          (p) =>
            p.kontrahentID === kontrahent?.id &&
            p.material === mat.material &&
            p.przeznaczenieID
        )?.cena;
        return {
          ...mat,
          firma: docWithMat?.firma?.nazwa || null,
          cena: cena != null ? Number(cena) : null,
          wartosc: cena != null ? Number(cena) * mat.quantity : null,
          cenaPrzezn: cenaPrzezn != null ? Number(cenaPrzezn) : null,
          wartoscPrzezn: cenaPrzezn != null ? Number(cenaPrzezn) * mat.quantity : null,
        };
      });
      const totalQty = materialsWithPrices.reduce(
        (sum, m) => sum + m.quantity,
        0
      );
      const totalValue = materialsWithPrices.reduce(
        (sum, m) => sum + (m.wartosc || 0),
        0
      );

      // Szczegółowe rekordy dokumentów dla klienta
      const docs = aggregateClientDocuments(filteredDocs, clientData.client);
      const docsEnriched = docs.map((rec) => {
        const cena = priceList.find(
          (p) =>
            p.kontrahentID === kontrahent?.id &&
            p.material === rec["Materiał"] &&
            !p.przeznaczenieID
        )?.cena;
        const cenaPrzezn = priceList.find(
          (p) =>
            p.kontrahentID === kontrahent?.id &&
            p.material === rec["Materiał"] &&
            p.przeznaczenie &&
            p.przeznaczenie.trim() === (rec["Przeznaczenie"] || "").trim()
        )?.cena;
        return {
          ...rec,
          "Cena z cennika": cena != null ? Number(cena) : null,
          "Wartość netto (cennikowa)":
            cena != null ? Number(cena) * rec["Ilość"] : null,
          "Cena wg przeznaczenia": cenaPrzezn != null ? Number(cenaPrzezn) : null,
          "Wartość wg przeznaczenia":
            cenaPrzezn != null ? Number(cenaPrzezn) * rec["Ilość"] : null,
        };
      });

      return {
        client: clientData.client,
        nip: kontrahent?.nip || null,
        adres: kontrahent?.adres || null,
        materials: materialsWithPrices,
        documents: docsEnriched,
        totalQty,
        totalValue,
      };
    });
  };

  // Funkcja eksportu do Excela
  const exportToExcel = (data, fileName = "export.xlsx") => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
  };

  const handleExport = () => {
    let exportData = [];
    if (selectedClient && clientDocRecords.length > 0) {
      exportData = clientDocRecords;
    } else if (selectedMaterial) {
      exportData = aggregateMaterialDocuments(
        filteredDocs,
        selectedMaterial.value
      );
    } else if (selectedFirma || selectedOddzial) {
      if (detailedStats.length > 0) {
        exportData = detailedStats;
      } else if (extendedStats.length > 0) {
        exportData = extendedStats;
      } else {
        exportData = filteredDocs.map((doc) => ({
          "Numer WZ": doc.numerWZ,
          Firma: doc.firma?.nazwa || "",
          "Egz.": doc.egz?.skrot || "",
          Odbiorca: doc.odbiorca?.nazwa || "",
          Transport: doc.transport?.nrRej || "",
          "Data wystawienia": new Date(doc.dataWystawienia).toLocaleDateString(
            "pl-PL"
          ),
          Wydał: doc.wydal || "",
          Opłacono: doc.oplacono ? "Tak" : "Nie",
          "Kwota brutto": doc.kwotaBrutto || 0,
          "Kwota netto": doc.kwotaNetto || 0,
          Uwagi: doc.uwagi || "",
          Przewoźnik: doc.przewoznik ? JSON.parse(doc.przewoznik).nazwa : null,
        }));
      }
    } else {
      exportData = filteredDocs.map((doc) => ({
        "Numer WZ": doc.numerWZ,
        Firma: doc.firma?.nazwa || "",
        "Egz.": doc.egz?.skrot || "",
        Odbiorca: doc.odbiorca?.nazwa || "",
        Transport: doc.transport?.nrRej || "",
        "Data wystawienia": new Date(doc.dataWystawienia).toLocaleDateString(
          "pl-PL"
        ),
        Wydał: doc.wydal || "",
        Opłacono: doc.oplacono ? "Tak" : "Nie",
        "Kwota brutto": doc.kwotaBrutto || 0,
        "Kwota netto": doc.kwotaNetto || 0,
        Uwagi: doc.uwagi || "",
        Przewoźnik: doc.przewoznik ? JSON.parse(doc.przewoznik).nazwa : null,
      }));
    }
    exportToExcel(exportData, "statystyki_wz.xlsx");
  };

  return (
    <div className="wz-stats-page">
      {/* Header */}
      <div className="wz-stats-header">
        <div>
          <h1><span className="wz-stats-header-icon">📊</span>{`Statystyki dokumentów ${docType}`}</h1>
          <p className="wz-stats-subtitle">Analiza i zestawienia dokumentów magazynowych</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {selectedClient && enrichedClientDocRecords.length > 0 && (
            <Button
              label="Drukuj kontrahenta"
              icon="pi pi-print"
              className="p-button-info"
              onClick={() => handlePrint("single")}
            />
          )}
          {(startDate || endDate) && filteredDocs.length > 0 && (
            <Button
              label="Drukuj wszystkich kontrahentów"
              icon="pi pi-print"
              className="p-button-warning"
              onClick={openPrintModal}
            />
          )}
          {(selectedClient ||
            selectedFirma ||
            selectedOddzial ||
            selectedMaterial) && (
            <Button
              label="Eksportuj do Excela"
              icon="pi pi-file-excel"
              className="p-button-success"
              onClick={handleExport}
            />
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="wz-stats-filters">
        <SelectButton
          allowEmpty={false}
          value={docType}
          onChange={(e) => {
            setDocType(e.value);
            setFilteredDocs([]);
            setClientDocRecords([]);
            setDetailedStats([]);
            setExtendedStats([]);
            setSelectedClient(null);
            setSelectedKontrahent(null);
            setSelectedFirma(null);
            setSelectedOddzial(null);
            setSelectedMaterial(null);
            setStartDate(null);
            setEndDate(null);
          }}
          options={options}
        />
        <Calendar
          value={startDate}
          onChange={(e) => setStartDate(e.value)}
          placeholder="Data początkowa"
          dateFormat="dd.mm.yy"
          showIcon
          showButtonBar
          className="calendarStats"
          locale="pl"
        />
        <Calendar
          value={endDate}
          onChange={(e) => setEndDate(e.value)}
          placeholder="Data końcowa"
          dateFormat="dd.mm.yy"
          showIcon
          showButtonBar
          locale="pl"
        />
        <Dropdown
          value={selectedFirma?.value}
          options={firmyOptions}
          onChange={(e) => setSelectedFirma(e.value ? e : null)}
          placeholder="Wybierz firmę"
          className="p-mb-3 dropdownStats"
          showClear
          panelClassName="dropdownStatsPanel"
        />
        <Dropdown
          value={selectedOddzial?.value}
          options={oddzialOptions}
          onChange={(e) => setSelectedOddzial(e.value ? e : null)}
          placeholder="Wybierz oddział"
          className="p-mb-3 dropdownStats"
          panelClassName="dropdownStatsPanel"
          showClear
        />
        <Dropdown
          value={selectedMaterial?.value}
          options={materialOptions}
          onChange={(e) => setSelectedMaterial(e.value ? e : null)}
          placeholder="Wybierz materiał"
          className="p-mb-3 dropdownStats"
          panelClassName="dropdownStatsPanel"
          showClear
        />
        <Dropdown
          value={selectedClient?.value}
          options={clientOptions}
          onChange={(e) => setSelectedClient(e.value ? e : null)}
          placeholder="Wybierz klienta"
          className="p-mb-3 dropdownStats"
          panelClassName="dropdownStatsPanel"
          showClear
        />
        <>
          <ToggleButton
            checked={isPrzewoznik}
            onChange={(e) => {
              setIsPrzewoznik(e.value);
            }}
            onLabel="Przewoźnicy"
            offLabel="Przewoźnicy"
            onIcon="pi pi-truck"
            offIcon="pi pi-truck"
            className="p-mb-3 dropdownStats"
          />
          {isPrzewoznik && (
            <MultiSelect
              value={selectedPrzewoznik?.value}
              options={przewoznicyOptions}
              onChange={(e) => {
                setSelectedPrzewoznik(e.value ? e : null);
              }}
              placeholder="Wybierz przewoźników"
              className="p-mb-3 dropdownStats"
              panelClassName="dropdownStatsPanel"
              showClear
            />
          )}
        </>
      </div>

      {isMobile && (
        <div className="wz-stats-mobile-hint">
          <i className="pi pi-info-circle" />
          <span>W celu uzyskania lepszej widoczności danych, skorzystaj z orientacji poziomej</span>
        </div>
      )}
      {selectedMaterial && !selectedClient ? (
        <div className="wz-stats-section">
          <h3 className="wz-stats-section-title">
            <i className="pi pi-box" />
            Statystyki dla materiału:{" "}
            {
              materialOptions.find((m) => m.value === selectedMaterial.value)
                ?.label
            }
          </h3>
          {(() => {
            const records = aggregateMaterialDocuments(
              filteredDocs,
              selectedMaterial.value
            );
            // console.log(records);
            return records.length > 0 ? (
              <>
                <DataTable
                  value={records}
                  paginator
                  rows={5}
                  emptyMessage="Brak danych"
                  rowClassName={(rowData) =>
                    rowData["Opłacono"] ? "green-row" : ""
                  }
                >
                  <Column field="Materiał" header="Materiał" />
                  <Column field="Klient" header="Klient" />
                  <Column field="Data wystawienia" header="Data wystawienia" />
                  <Column field="Numer WZ" header={`Numer ${docType}`} />
                  <Column
                    field="przewoznik"
                    header="Przewoźnik"
                    body={(rowData) => (
                      <span>
                        {rowData["Przewoźnik"] ? rowData["Przewoźnik"] : ""}
                      </span>
                    )}
                  />
                  <Column
                    field="Opłacono"
                    header="Opłacono"
                    body={(rowData) => (
                      <span>{rowData["Opłacono"] ? "Tak" : "Nie"}</span>
                    )}
                  />
                  <Column
                    field="Kwota brutto"
                    header="Kwota brutto"
                    body={(rowData) => (
                      <span>{rowData["Kwota brutto"]} zł</span>
                    )}
                  />
                  <Column
                    field="Kwota netto"
                    header="Kwota netto"
                    body={(rowData) => <span>{rowData["Kwota netto"]} zł</span>}
                  />
                  <Column field="Uwagi" header="Uwagi" />
                </DataTable>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={records}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="Klient" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      // barSize={60}
                      // label={{ position: "top" }}
                      dataKey="Numer WZ"
                      fill="#82ca9d"
                      name={`Numer ${docType}`}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : (
              <p>
                Brak statystyk dla wybranego materiału w wybranym zakresie dat.
              </p>
            );
          })()}
        </div>
      ) : selectedClient ? (
        <div className="wz-stats-section">
          <h3 className="wz-stats-section-title">
            <i className="pi pi-user" />
            {`Statystyki dla klienta: ${selectedClient.value} (${
              selectedKontrahent?.nip ? `NIP: ${selectedKontrahent?.nip},` : ""
            } ${selectedKontrahent?.adres || "brak"}
           ${
             selectedKontrahent?.email
               ? `, Email: ${selectedKontrahent?.email}`
               : ""
           })`}
          </h3>
          {enrichedClientDocRecords.length > 0 ? (
            <>
              {/* Szczegółowa tabela – pozostawiamy bez zmian */}
              <DataTable
                value={enrichedClientDocRecords}
                paginator
                rows={5}
                emptyMessage="Brak danych"
              >
                <Column field="Materiał" header="Materiał" />
                <Column
                  field="Ilość"
                  header="Ilość"
                  body={(rowData) => <span>{rowData["Ilość"]} t</span>}
                />
                <Column field="Data wystawienia" header="Data wystawienia" />
                <Column field="Numer WZ" header={`Numer ${docType}`} />
                <Column
                  field="Opłacono"
                  header="Opłacono"
                  body={(rowData) => (
                    <span>
                      {rowData["Opłacono"] ? (
                        <i
                          className="pi pi-check"
                          style={{ fontSize: "1rem" }}
                        ></i>
                      ) : (
                        ""
                      )}
                    </span>
                  )}
                />
                <Column
                  field="Kwota brutto"
                  header="Kwota brutto"
                  body={(rowData) => <span>{rowData["Kwota brutto"]} zł</span>}
                />
                <Column
                  field="Kwota netto"
                  header="Kwota netto"
                  body={(rowData) => <span>{rowData["Kwota netto"]} zł</span>}
                />
                <Column
                  field="Cena z cennika"
                  header="Cena z cennika"
                  body={(rowData) =>
                    rowData["Cena z cennika"] != null
                      ? `${rowData["Cena z cennika"]} zł`
                      : "-"
                  }
                />
                <Column
                  field="Wartość netto (cennikowa)"
                  header="Wartość netto (cennikowa)"
                  body={(rowData) =>
                    rowData["Wartość netto (cennikowa)"] != null
                      ? `${rowData["Wartość netto (cennikowa)"].toFixed(2)} zł`
                      : "-"
                  }
                />
                <Column field="Przeznaczenie" header="Przeznaczenie" />
                <Column
                  field="Cena wg przeznaczenia"
                  header="Cena wg przeznaczenia"
                  body={(rowData) =>
                    rowData["Cena wg przeznaczenia"] != null
                      ? `${rowData["Cena wg przeznaczenia"]} zł`
                      : "–"
                  }
                />
                <Column
                  field="Wartość wg przeznaczenia"
                  header="Wartość wg przeznaczenia"
                  body={(rowData) =>
                    rowData["Wartość wg przeznaczenia"] != null
                      ? `${rowData["Wartość wg przeznaczenia"].toFixed(2)} zł`
                      : "–"
                  }
                />
                <Column field="Uwagi" header="Uwagi" />
              </DataTable>
              {/* Agregacja danych według materiału */}
              {(() => {
                const aggregated = enrichedClientDocRecords.reduce(
                  (acc, record) => {
                    const material = record["Materiał"];
                    const qty = Number(record["Ilość"]) || 0;
                    if (acc[material]) {
                      acc[material]["Ilość"] += qty;
                    } else {
                      acc[material] = {
                        Materiał: material,
                        Ilość: qty,
                        "Cena z cennika": record["Cena z cennika"],
                        "Wartość netto (cennikowa)":
                          record["Wartość netto (cennikowa)"],
                      };
                    }
                    return acc;
                  },
                  {}
                );
                const aggregatedArray = Object.values(aggregated);

                return (
                  <>
                    <h4>Podsumowanie wg materiału</h4>
                    <DataTable
                      value={aggregatedArray}
                      paginator
                      rows={5}
                      emptyMessage="Brak danych"
                    >
                      <Column field="Materiał" header="Materiał" />
                      <Column
                        field="Ilość"
                        header="Ilość"
                        body={(rowData) => (
                          <span>{rowData["Ilość"]?.toFixed(2)} t</span>
                        )}
                      />
                      <Column
                        field="Cena z cennika"
                        header="Cena z cennika"
                        body={(rowData) =>
                          rowData["Cena z cennika"] != null
                            ? `${rowData["Cena z cennika"]} zł`
                            : "-"
                        }
                      />
                      <Column
                        field="Wartość netto (cennikowa)"
                        header="Wartość netto (cennikowa)"
                        body={(rowData) =>
                          rowData["Ilość"] != null
                            ? `${(
                                rowData["Ilość"] * rowData["Cena z cennika"]
                              ).toFixed(2)} zł`
                            : "-"
                        }
                      />
                    </DataTable>
                    <h3 className="wz-stats-section-title">
                    <i className="pi pi-user" />
                    {`Klient: ${selectedClient.value} (${
                      selectedKontrahent?.nip
                        ? `NIP: ${selectedKontrahent?.nip},`
                        : ""
                    } ${selectedKontrahent?.adres || "brak"}
           ${
             selectedKontrahent?.email
               ? `, Email: ${selectedKontrahent?.email}`
               : ""
           })`}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={aggregatedArray}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="Materiał" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="Ilość"
                          fill="#82ca9d"
                          name="Ilość towaru"
                          //zaokrąglij ilość do dwóch miejsc po przecinku
                          barSize={60}
                          label={{
                            position: "top",
                            formatter: (value) => value.toFixed(2),
                          }}
                          formatter={(value) => value.toFixed(2)}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                );
              })()}
            </>
          ) : (
            <p>Brak statystyk dla wybranego klienta w wybranym zakresie dat.</p>
          )}
        </div>
      ) : selectedFirma || selectedOddzial ? (
        <div className="wz-stats-section">
          <h3 className="wz-stats-section-title"><i className="pi pi-sitemap" />Statystyki z podziałem na klienta i oddział</h3>
          {detailedStats.length > 0 ? (
            <DataTable
              value={detailedStats}
              paginator
              rows={5}
              emptyMessage="Brak danych"
            >
              <Column field="firma" header="Firma" />
              <Column field="client" header="Klient" />
              <Column field="oddzial" header="Oddział" />
              <Column field="material" header="Materiał" />
              <Column
                field="quantity"
                header="Ilość"
                body={(rowData) => <span>{rowData.quantity.toFixed(2)} t</span>}
              />
            </DataTable>
          ) : (
            <p>Brak statystyk dla wybranych parametrów.</p>
          )}
          <h3 className="wz-stats-section-title"><i className="pi pi-chart-bar" />Rozszerzone statystyki: Klient, Materiał, Kopalnia, Firma</h3>
          {extendedStats.length > 0 ? (
            <DataTable
              value={extendedStats}
              paginator
              rows={5}
              emptyMessage="Brak danych"
            >
              <Column field="client" header="Klient" />
              <Column field="material" header="Materiał" />
              <Column field="kopalnia" header="Kopalnia" />
              <Column field="firma" header="Firma" />
              <Column
                field="quantity"
                header="Ilość"
                body={(rowData) => <span>{rowData.quantity.toFixed(2)} t</span>}
              />
            </DataTable>
          ) : (
            <p>Brak rozszerzonych statystyk dla wybranych parametrów.</p>
          )}
          <h3 className="wz-stats-section-title"><i className="pi pi-th-large" />Podsumowanie materiałów wg oddziałów</h3>
          {statsByOddzialMaterial.length > 0 ? (
            <DataTable
              value={statsByOddzialMaterial}
              paginator
              rows={5}
              emptyMessage="Brak danych"
            >
              <Column field="oddzial" header="Oddział" />
              <Column field="material" header="Materiał" />
              <Column
                field="quantity"
                header="Ilość"
                body={(rowData) => <span>{rowData.quantity.toFixed(2)} t</span>}
              />
            </DataTable>
          ) : (
            <p>Brak danych do podsumowania materiałów wg oddziałów.</p>
          )}

          <h3 className="wz-stats-section-title"><i className="pi pi-calculator" />Łączna ilość materiału na oddziały</h3>
          {statsByOddzialTotal.length > 0 ? (
            <DataTable
              value={statsByOddzialTotal}
              paginator
              rows={5}
              emptyMessage="Brak danych"
            >
              <Column field="oddzial" header="Oddział" />
              <Column
                field="quantity"
                header="Ilość łączna"
                body={(rowData) => <span>{rowData.quantity.toFixed(2)} t</span>}
              />
            </DataTable>
          ) : (
            <p>Brak danych do łącznego podsumowania materiału na oddziały.</p>
          )}
          {forecastData.length > 0 && (
            <>
              <h3 className="wz-stats-section-title"><i className="pi pi-chart-line" />Prognoza wydobycia (na podstawie danych miesięcznych)</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={forecastData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="quantity"
                    fill="#8884d8"
                    name="Ilość wydobyta"
                    isAnimationActive={true}
                    label={{
                      position: "top",
                      formatter: (value) => value.toFixed(2),
                    }}
                    formatter={(value) => value.toFixed(2)}
                  >
                    {
                      // Odróżnij słupki prognozowane (np. szare)
                      forecastData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.forecast ? "#7c7c7cff" : "#8884d8"}
                        />
                      ))
                    }
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p style={{ fontStyle: "italic", marginTop: "0.5rem" }}>
                Prognoza oparta na wszystkich dostępnych danych historycznych,
                niezależnie od aktualnych filtrów.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="wz-stats-empty">
          <i className="pi pi-filter" />
          <p>Wybierz klienta, firmę, oddział lub materiał, aby zobaczyć statystyki.</p>
        </div>
      )}

      {/* Sekcja drukowania — wybrany kontrahent (szczegóły + podsumowanie) */}
      {printMode === "single" && selectedClient && (() => {
        const singleData = getAllClientsSummary().find(
          (c) => c.client === selectedClient.value
        );
        if (!singleData) return null;
        return (
          <div className="wz-stats-print-section">
            <h2>Statystyki dokumentów {docType}</h2>
            <p className="wz-stats-print-period">
              Okres:{" "}
              {startDate ? new Date(startDate).toLocaleDateString("pl-PL") : "od początku"}{" "}
              –{" "}
              {endDate ? new Date(endDate).toLocaleDateString("pl-PL") : "do dziś"}
            </p>
            <div className="wz-stats-print-client">
              <h3>{singleData.client}</h3>
              <p style={{ margin: "0 0 6pt", fontSize: "9pt" }}>
                {singleData.nip ? `NIP: ${singleData.nip}` : ""}
                {singleData.nip && singleData.adres ? ", " : ""}
                {singleData.adres || ""}
              </p>

              <h4 style={{ margin: "6pt 0 3pt", fontSize: "9pt" }}>Zestawienie dokumentów</h4>
              <table className="wz-stats-print-table">
                <thead>
                  <tr>
                    <th>Materiał</th>
                    <th>Ilość (t)</th>
                    <th>Data wystawienia</th>
                    <th>Numer {docType}</th>
                    <th>Firma</th>
                    <th>Opłacono</th>
                    <th>Kwota brutto (zł)</th>
                    <th>Kwota netto (zł)</th>
                    <th>Cena z cennika (zł)</th>
                    <th>Wartość netto (zł)</th>
                    <th>Przeznaczenie</th>
                    <th>Cena wg przezn. (zł)</th>
                    <th>Wartość wg przezn. (zł)</th>
                    <th>Uwagi</th>
                  </tr>
                </thead>
                <tbody>
                  {singleData.documents.map((rec, i) => (
                    <tr key={i}>
                      <td>{rec["Materiał"]}</td>
                      <td>{(rec["Ilość"] || 0).toFixed(2)}</td>
                      <td>{rec["Data wystawienia"]}</td>
                      <td>{rec["Numer WZ"]}</td>
                      <td>{rec["Firma"]}</td>
                      <td>{rec["Opłacono"] ? "Tak" : "Nie"}</td>
                      <td>{rec["Kwota brutto"]}</td>
                      <td>{rec["Kwota netto"]}</td>
                      <td>{rec["Cena z cennika"] != null ? rec["Cena z cennika"] : "—"}</td>
                      <td>{rec["Wartość netto (cennikowa)"] != null ? rec["Wartość netto (cennikowa)"].toFixed(2) : "—"}</td>
                      <td>{rec["Przeznaczenie"] || "—"}</td>
                      <td>{rec["Cena wg przeznaczenia"] != null ? rec["Cena wg przeznaczenia"] : "—"}</td>
                      <td>{rec["Wartość wg przeznaczenia"] != null ? rec["Wartość wg przeznaczenia"].toFixed(2) : "—"}</td>
                      <td>{rec["Uwagi"]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{ margin: "8pt 0 3pt", fontSize: "9pt" }}>Podsumowanie wg materiału</h4>
              <table className="wz-stats-print-table">
                <thead>
                  <tr>
                    <th>Materiał</th>
                    <th>Ilość (t)</th>
                    <th>Cena z cennika (zł)</th>
                    <th>Wartość netto (zł)</th>
                  </tr>
                </thead>
                <tbody>
                  {singleData.materials.map((mat, i) => (
                    <tr key={i}>
                      <td>{mat.material}</td>
                      <td>{mat.quantity.toFixed(2)}</td>
                      <td>{mat.cena != null ? `${mat.cena}` : "—"}</td>
                      <td>{mat.wartosc != null ? mat.wartosc.toFixed(2) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Razem</strong></td>
                    <td><strong>{singleData.totalQty.toFixed(2)}</strong></td>
                    <td></td>
                    <td><strong>{singleData.totalValue > 0 ? singleData.totalValue.toFixed(2) : "—"}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="wz-stats-print-period">Liczba dokumentów: {singleData.documents.length}</p>
          </div>
        );
      })()}

      {/* Sekcja drukowania — wszyscy kontrahenci */}
      {printMode === "all" && (
        <div className="wz-stats-print-section">
          <h2>Statystyki dokumentów {docType} - Wszyscy kontrahenci</h2>
          <p className="wz-stats-print-period">
            Okres:{" "}
            {startDate ? new Date(startDate).toLocaleDateString("pl-PL") : "od początku"}{" "}
            –{" "}
            {endDate ? new Date(endDate).toLocaleDateString("pl-PL") : "do dziś"}
          </p>
          <p className="wz-stats-print-period">Liczba dokumentów: {filteredDocs.length}</p>
          {getAllClientsSummary()
            .filter((clientData) => {
              const pc = printClients.find((c) => c.client === clientData.client);
              return pc ? pc.selected : true;
            })
            .map((clientData, idx) => {
              const pc = printClients.find((c) => c.client === clientData.client);
              const showOplacono = pc?.showOplacono || false;
              return (
                <div key={idx} className="wz-stats-print-client">
                  <h3>{clientData.client}</h3>
                  <p style={{ margin: "0 0 6pt", fontSize: "9pt" }}>
                    {clientData.nip ? `NIP: ${clientData.nip}` : ""}
                    {clientData.nip && clientData.adres ? ", " : ""}
                    {clientData.adres || ""}
                  </p>

                  {!printSummaryOnly && (
                    <>
                      <h4 style={{ margin: "6pt 0 3pt", fontSize: "9pt" }}>Zestawienie dokumentów</h4>
                      <table className="wz-stats-print-table">
                        <thead>
                          <tr>
                            <th>Materiał</th>
                            <th>Ilość (t)</th>
                            <th>Data wystawienia</th>
                            <th>Numer {docType}</th>
                            <th>Firma</th>
                            {showOplacono && <th>Opłacono</th>}
                            <th>Uwagi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientData.documents.map((rec, i) => (
                            <tr key={i}>
                              <td>{rec["Materiał"]}</td>
                              <td>{(rec["Ilość"] || 0).toFixed(2)}</td>
                              <td>{rec["Data wystawienia"]}</td>
                              <td>{rec["Numer WZ"]}</td>
                              <td>{rec["Firma"]}</td>
                              {showOplacono && <td>{rec["Opłacono"] ? "Tak" : "Nie"}</td>}
                              <td>{rec["Uwagi"]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  <h4 style={{ margin: "8pt 0 3pt", fontSize: "9pt" }}>Podsumowanie wg materiału</h4>
                  <table className="wz-stats-print-table">
                    <thead>
                      <tr>
                        <th>Materiał</th>
                        <th>Firma</th>
                        <th>Ilość (t)</th>
                        <th>Cena z cennika (zł)</th>
                        <th>Cena wg przezn. (zł)</th>
                        <th>Wartość wg przezn. (zł)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientData.materials.map((mat, i) => (
                        <tr key={i}>
                          <td>{mat.material}</td>
                          <td>{mat.firma || "—"}</td>
                          <td>{mat.quantity.toFixed(2)}</td>
                          <td>{mat.cena != null ? `${mat.cena}` : "—"}</td>
                          <td>{mat.cenaPrzezn != null ? `${mat.cenaPrzezn}` : "—"}</td>
                          <td>{mat.wartoscPrzezn != null ? mat.wartoscPrzezn.toFixed(2) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Razem</strong></td>
                        <td></td>
                        <td><strong>{clientData.totalQty.toFixed(2)}</strong></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })}
        </div>
      )}

      {/* Modal wyboru kontrahentów do wydruku */}
      <Dialog
        header="Wybierz kontrahentów do wydruku"
        visible={showPrintModal}
        onHide={() => setShowPrintModal(false)}
        style={{ width: "50vw" }}
        footer={
          <div>
            <Button
              label="Anuluj"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowPrintModal(false)}
            />
            <Button
              label="Drukuj"
              icon="pi pi-print"
              onClick={handlePrintFromModal}
              disabled={!printClients.some((c) => c.selected)}
            />
          </div>
        }
      >
        <div style={{ marginBottom: "1rem", padding: "0.5rem", background: "#f8f9fa", borderRadius: "4px", border: "1px solid #dee2e6" }}>
          <Checkbox
            inputId="summaryOnly"
            checked={printSummaryOnly}
            onChange={(e) => setPrintSummaryOnly(e.checked)}
          />
          <label htmlFor="summaryOnly" style={{ marginLeft: "0.5rem", fontWeight: "bold", cursor: "pointer" }}>
            Tylko podsumowania (bez zestawienia dokumentów)
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Checkbox
            inputId="selectAll"
            checked={printClients.length > 0 && printClients.every((c) => c.selected)}
            onChange={(e) =>
              setPrintClients(printClients.map((c) => ({ ...c, selected: e.checked })))
            }
          />
          <label htmlFor="selectAll" style={{ marginLeft: "0.5rem", fontWeight: "bold", cursor: "pointer" }}>
            Zaznacz / Odznacz wszystkich
          </label>
        </div>
        <DataTable value={printClients} scrollable scrollHeight="400px" size="small">
          <Column
            header="Drukuj"
            body={(rowData, { rowIndex }) => (
              <Checkbox
                checked={rowData.selected}
                onChange={(e) => {
                  const updated = [...printClients];
                  updated[rowIndex].selected = e.checked;
                  setPrintClients(updated);
                }}
              />
            )}
            style={{ width: "5rem", textAlign: "center" }}
          />
          <Column field="client" header="Kontrahent" />
          <Column
            header="Pokaż Opłacono"
            body={(rowData, { rowIndex }) => (
              <Checkbox
                checked={rowData.showOplacono}
                onChange={(e) => {
                  const updated = [...printClients];
                  updated[rowIndex].showOplacono = e.checked;
                  setPrintClients(updated);
                }}
              />
            )}
            style={{ width: "10rem", textAlign: "center" }}
          />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default WZStatisticsExtended;
